// SourceSelectorPanel.js
import React, { useState, useEffect, useRef } from 'react';
import '../styles/SourceSelectorPanel.css';

function SourceSelectorPanel({ 
  availableSources = [], 
  selectedSources = [],
  toggleSourceSelection,
  clearSelectedSources,
  onClose,
  theme
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSources, setFilteredSources] = useState([]);
  const panelRef = useRef(null);

  // Filter sources based on search term
  useEffect(() => {
    // First put selected sources at the top
    const sortedSources = [...availableSources].sort((a, b) => {
      const aSelected = selectedSources.includes(a.filename);
      const bSelected = selectedSources.includes(b.filename);
      
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });
    
    // Then filter based on search term - removed tag search functionality
    const filtered = sortedSources.filter(source => 
      source.filename.toLowerCase().includes(searchTerm.toLowerCase()) || 
      (source.title && source.title.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    setFilteredSources(filtered);
  }, [searchTerm, availableSources, selectedSources]);

  // Handle click outside to close panel
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  return (
    <div className="source-selector-overlay">
      <div className="source-selector-panel" ref={panelRef}>
        <div className="source-panel-header">
          <h2>Select Data Sources</h2>
          <button 
            className="panel-close-btn" 
            onClick={onClose}
          >
            <span className="material-icons">close</span>
          </button>
        </div>
        
        <div className="source-panel-search">
          <span className="material-icons search-icon">search</span>
          <input
            type="text"
            placeholder="Search sources..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button 
              className="clear-search-btn" 
              onClick={() => setSearchTerm('')}
            >
              <span className="material-icons">clear</span>
            </button>
          )}
        </div>
        
        <div className="source-panel-info">
          <p>Select specific sources to search in, or leave all unselected to search across all sources.</p>
          {selectedSources.length > 0 && (
            <button 
              className="clear-sources-btn" 
              onClick={clearSelectedSources}
            >
              <span className="material-icons">clear_all</span> Clear All
            </button>
          )}
        </div>
        
        <div className="source-panel-list">
          {filteredSources.length === 0 ? (
            <div className="no-sources">
              {searchTerm ? "No matching sources found" : "No document sources available. Upload documents to enable source selection."}
            </div>
          ) : (
            filteredSources.map(source => (
              <div 
                key={source.filename} 
                className={`source-panel-item ${selectedSources.includes(source.filename) ? 'selected' : ''}`}
                onClick={() => toggleSourceSelection(source.filename)}
              >
                <div className="source-item-checkbox">
                  {selectedSources.includes(source.filename) ? (
                    <span className="material-icons">check_circle</span>
                  ) : (
                    <span className="material-icons">radio_button_unchecked</span>
                  )}
                </div>
                <div className="source-item-details">
                  <div className="source-item-name">
                    {source.title || source.filename}
                  </div>
                  <div className="source-item-meta">
                    <span className="source-type">{source.type}</span>
                    <span className="source-pages">{source.page_count} pages</span>
                  </div>
                </div>
                {/* Removed tag display section */}
              </div>
            ))
          )}
        </div>

        <div className="source-panel-footer">
          <button className="source-panel-action-btn" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default SourceSelectorPanel;